import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import IndustryCodeSelect from '../components/prospector/IndustryCodeSelect.tsx';
import SaveCompany from '../components/prospector/prospecting/SaveCompany.tsx';
import BulkSaveComponent from '../components/prospector/prospecting/BulkSaveComponent.tsx';
import { savedCompaniesApi } from '../services/prospector/savedCompanies.ts';

// Note: We're using a pure JavaScript implementation for now
// In the future, we can gradually migrate to TypeScript components

// We'll need to create a JavaScript version of the CompanySearch component
// that imports the TypeScript IndustryCodeSelect component
const CompanySearch = ({ onSearch, isLoading, initialParams }) => {
  const [searchParams, setSearchParams] = useState(initialParams || {
    navn: '',
    organisasjonsnummer: '',
    antallAnsatte: '',
    naeringskode: '',
    kommunenummer: '',
    sidestorrelse: 20,
    side: 0
  });

  // Get industry codes from the initial params if available
  const initialCodes = initialParams?.naeringskode ? initialParams.naeringskode.split(',') : [];

  // State to track selected industry codes
  const [selectedIndustryCodes, setSelectedIndustryCodes] = useState(initialCodes);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle industry code selection
  const handleIndustryCodeSelect = (codes) => {
    setSelectedIndustryCodes(codes);
    
    // Update the naeringskode parameter
    if (codes.length > 0) {
      setSearchParams(prev => ({
        ...prev,
        naeringskode: codes.join(',')
      }));
    } else {
      setSearchParams(prev => ({
        ...prev,
        naeringskode: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Start with a copy of form data
    const params = {...searchParams};
    
    // Handle employee count range conversion
    if (params.antallAnsatte) {
      // Special case for "250+" format
      if (params.antallAnsatte === "250+") {
        // For 250+ we only set the lower bound, not the upper bound
        params.fraAntallAnsatte = "250";
        delete params.antallAnsatte;
      } 
      // Check if it's a standard range format like "5-9" or "50-99"
      else {
        const rangeMatch = params.antallAnsatte.match(/^(\d+)-(\d+)$/);
        if (rangeMatch) {
          // Extract min and max values
          const minValue = rangeMatch[1];
          const maxValue = rangeMatch[2];
          
          // Set the API-compatible parameters
          params.fraAntallAnsatte = minValue;
          params.tilAntallAnsatte = maxValue;
          delete params.antallAnsatte;
        }
      }
    }
    
    // Filter out empty form values
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== '')
    );
    
    onSearch(filteredParams);
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Søk etter bedrifter</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="navn" className="block text-sm font-medium text-gray-700 mb-1">
              Bedriftsnavn
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              value={searchParams.navn || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-white"
              placeholder="Bedriftsnavn (f.eks. Learnist AS)"
            />
            <p className="text-xs text-gray-500 mt-1">
              For mer presist søk, bruk fullt navn med AS, ENK, etc.
            </p>
          </div>
          
          <div>
            <label htmlFor="organisasjonsnummer" className="block text-sm font-medium text-gray-700 mb-1">
              Organisasjonsnummer
            </label>
            <input
              type="text"
              id="organisasjonsnummer"
              name="organisasjonsnummer"
              value={searchParams.organisasjonsnummer || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-white"
              placeholder="9 siffer (eks. 926015532)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Det mest presise søket er på organisasjonsnummer
            </p>
          </div>
          
          <div>
            <label htmlFor="antallAnsatte" className="block text-sm font-medium text-gray-700 mb-1">
              Antall ansatte
            </label>
            <select
              id="antallAnsatte"
              name="antallAnsatte"
              value={searchParams.antallAnsatte || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-white"
            >
              <option value="">Alle størrelser</option>
              <option value="1-4">1-4 ansatte</option>
              <option value="5-9">5-9 ansatte</option>
              <option value="10-19">10-19 ansatte</option>
              <option value="20-49">20-49 ansatte</option>
              <option value="50-99">50-99 ansatte</option>
              <option value="100-249">100-249 ansatte</option>
              <option value="250+">250+ ansatte</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="kommunenummer" className="block text-sm font-medium text-gray-700 mb-1">
              Kommunenummer
            </label>
            <input
              type="text"
              id="kommunenummer"
              name="kommunenummer"
              value={searchParams.kommunenummer || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 bg-white"
              placeholder="F.eks. 0301 (Oslo)"
            />
          </div>
        </div>
        
        {/* Industry code selector - tree view */}
        <div className="mt-4">
          <label htmlFor="naeringskode" className="block text-sm font-medium text-gray-700 mb-1">
            Næringskode
          </label>
          <div className="industry-code-tree-selector">
            <IndustryCodeSelect
              onSelectIndustryCodes={handleIndustryCodeSelect}
              initialSelectedCodes={selectedIndustryCodes}
            />
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 font-medium"
          >
            {isLoading ? 'Søker...' : 'Søk'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Prospector Page
const Prospector = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchParams, setCurrentSearchParams] = useState(null);
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 20
  });
  const [error, setError] = useState(null);
  // State for tracking which company is being added to a list
  const [activeCompany, setActiveCompany] = useState(null);
  const [showSaveCompany, setShowSaveCompany] = useState(false);
  // Bulk save state
  const [showBulkSave, setShowBulkSave] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  // State to track if we should show search form or results
  const [showSearchForm, setShowSearchForm] = useState(true);
  // We now use page navigation for company details instead of a modal

  // Load saved search results and parameters from localStorage when the component mounts
  useEffect(() => {
    const savedSearchResults = localStorage.getItem('prospector_search_results');
    const savedSearchParams = localStorage.getItem('prospector_search_params');
    const savedPagination = localStorage.getItem('prospector_pagination');
    const savedLastRoute = localStorage.getItem('prospector_last_route');
    let hasValidSavedData = false;

    // Check if we should redirect to the last visited route
    // But don't redirect if user intentionally clicked back to search (check for specific flags)
    const dontRedirect = localStorage.getItem('prospector_dont_redirect');
    if (dontRedirect) {
      localStorage.removeItem('prospector_dont_redirect');
      console.log('Skipping redirect due to intentional navigation back');
    } else if (savedLastRoute && savedLastRoute !== '/prospector' && window.location.pathname === '/prospector') {
      console.log('Redirecting to last visited Prospector route:', savedLastRoute);
      navigate(savedLastRoute, { replace: true });
      return;
    }

    if (savedSearchResults) {
      try {
        const results = JSON.parse(savedSearchResults);
        if (results && results.length > 0) {
          setSearchResults(results);
          hasValidSavedData = true;
          console.log('Loaded saved search results from localStorage');
        }
      } catch (err) {
        console.error('Error parsing saved search results:', err);
      }
    }

    if (savedSearchParams) {
      try {
        setCurrentSearchParams(JSON.parse(savedSearchParams));
        console.log('Loaded saved search parameters from localStorage');
      } catch (err) {
        console.error('Error parsing saved search parameters:', err);
      }
    }

    if (savedPagination) {
      try {
        setPagination(JSON.parse(savedPagination));
        console.log('Loaded saved pagination from localStorage');
      } catch (err) {
        console.error('Error parsing saved pagination:', err);
      }
    }

    // If we have valid saved data, show results instead of search form
    if (hasValidSavedData) {
      setShowSearchForm(false);
    }
  }, [navigate]);

  // Track current route for restoration
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/prospector')) {
      localStorage.setItem('prospector_last_route', currentPath);
    }
  }, []);

  // Also save the route when navigating to company details
  const handleViewCompany = (organizationNumber) => {
    const detailRoute = `/prospector/${organizationNumber}`;
    localStorage.setItem('prospector_last_route', detailRoute);
    navigate(detailRoute);
  };

  const handleSearch = async (params) => {
    setIsLoading(true);
    setError(null);

    // Save the current search parameters for pagination
    setCurrentSearchParams(params);

    // For first-page searches, clear any previous saved results
    if (!params.side || params.side === 0) {
      localStorage.removeItem('prospector_search_results');
    }

    // Add debugging to see what parameters are being sent
    console.log('Search parameters:', params);

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        setError('Authentication required. Please log in.');
        setIsLoading(false);
        return;
      }

      // Add token to request headers
      const config = {
        headers: { 'x-auth-token': token },
        params
      };

      console.log('Making request to /api/prospector/companies with config:', config);
      const response = await axios.get('/api/prospector/companies', config);
      console.log('API response status:', response.status);
      console.log('API response data:', response.data);

      if (response.data && response.data._embedded && response.data._embedded.enheter) {
        const results = response.data._embedded.enheter;
        const paginationData = {
          totalElements: response.data.page.totalElements,
          totalPages: response.data.page.totalPages,
          currentPage: response.data.page.number,
          pageSize: response.data.page.size
        };

        // Update state
        setSearchResults(results);
        setPagination(paginationData);
        setShowSearchForm(false); // Show results instead of search form

        // Save to localStorage for persistence
        try {
          localStorage.setItem('prospector_search_results', JSON.stringify(results));
          localStorage.setItem('prospector_search_params', JSON.stringify(params));
          localStorage.setItem('prospector_pagination', JSON.stringify(paginationData));
          console.log('Saved search results and parameters to localStorage');
        } catch (err) {
          console.error('Error saving search results to localStorage:', err);
        }
      } else {
        console.log('No results found or different response format:', response.data);
        setSearchResults([]);
        setPagination({
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 20
        });

        // Clear localStorage values since no results found
        localStorage.removeItem('prospector_search_results');
        localStorage.removeItem('prospector_search_params');
        localStorage.removeItem('prospector_pagination');
      }
    } catch (err) {
      console.error('Error searching companies:', err);
      setError(err.message || 'Failed to search companies');
    } finally {
      setIsLoading(false);
    }
  };

  // We now use page navigation for company details

  const handleAddToList = (company) => {
    setActiveCompany(company);
    setShowSaveCompany(true);
  };

  const handleSaveCompanySuccess = () => {
    // Close the save company UI after successful save
    setShowSaveCompany(false);
    setActiveCompany(null);
  };

  const handleBulkSaveToList = async (listId) => {
    setBulkSaving(true);
    try {
      let allCompanies = [];
      
      // If we have more than one page, fetch all results
      if (pagination.totalPages > 1) {
        console.log(`Fetching all ${pagination.totalElements} companies across ${pagination.totalPages} pages...`);
        
        // Fetch all pages
        for (let page = 0; page < pagination.totalPages; page++) {
          console.log(`Fetching page ${page + 1} of ${pagination.totalPages}...`);
          
          try {
            // Get auth token from localStorage
            const token = localStorage.getItem('token');
            if (!token) {
              throw new Error('Authentication required. Please log in.');
            }

            // Create parameters for this page
            const pageParams = {
              ...currentSearchParams,
              side: page,
              sidestorrelse: pagination.pageSize
            };

            // Add token to request headers
            const config = {
              headers: { 'x-auth-token': token },
              params: pageParams
            };

            const response = await axios.get('/api/prospector/companies', config);
            
            if (response.data && response.data._embedded && response.data._embedded.enheter) {
              allCompanies = allCompanies.concat(response.data._embedded.enheter);
            }
          } catch (pageError) {
            console.error(`Error fetching page ${page + 1}:`, pageError);
            // Continue with other pages even if one fails
          }
        }
        
        console.log(`Successfully fetched ${allCompanies.length} total companies`);
      } else {
        // Only one page, use current results
        allCompanies = searchResults;
      }

      // Convert companies to the format expected by the API
      const companies = allCompanies.map(company => ({
        organizationNumber: company.organisasjonsnummer,
        companyName: company.navn,
        industryCode: company.naeringskode1?.kode,
        industryDescription: company.naeringskode1?.beskrivelse,
        antallAnsatte: company.antallAnsatte !== undefined ? Number(company.antallAnsatte) : undefined,
        dateAdded: new Date(),
      }));

      console.log(`Saving ${companies.length} companies to list...`);
      const result = await savedCompaniesApi.bulkAddCompaniesToList(listId, companies);
      
      if (result.errorCount > 0) {
        alert(`Saved ${result.saved} companies successfully. ${result.errorCount} failed to save.`);
      } else {
        alert(`Successfully saved all ${result.saved} companies to list!`);
      }
      
      setShowBulkSave(false);
    } catch (err) {
      console.error('Error saving companies to list:', err);
      alert(`Failed to save companies: ${err.message}`);
    } finally {
      setBulkSaving(false);
    }
  };

  // Company details are now handled in ProspectorDetail.js

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Prospector</h1>
          <p className="mt-1 text-sm text-gray-500">
            Search for companies using Brønnøysundregistrene data
          </p>
        </div>
        <button
          onClick={() => navigate('/prospector/lists')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          My Lists
        </button>
      </div>
      
      {showSearchForm && (
        <div className="mb-8">
          <CompanySearch
            onSearch={handleSearch}
            isLoading={isLoading}
            initialParams={currentSearchParams}
          />
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {searchResults.length > 0 && !showSearchForm ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="flex justify-between items-center px-4 py-3 sm:px-6 bg-gray-50">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Results</h2>
              {!currentSearchParams && (
                <div className="flex items-center">
                  <span className="text-xs text-gray-500 italic mr-2">Showing saved results from previous search</span>
                  <button
                    onClick={() => {
                      // Clear saved search results and reset the state
                      localStorage.removeItem('prospector_search_results');
                      localStorage.removeItem('prospector_search_params');
                      localStorage.removeItem('prospector_pagination');
                      localStorage.removeItem('prospector_last_route');
                      localStorage.setItem('prospector_dont_redirect', 'true');
                      setSearchResults([]);
                      setCurrentSearchParams(null);
                      setShowSearchForm(true);
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear
                  </button>
                </div>
              )}
              <span className="text-sm text-gray-700">
                Showing {searchResults.length} of {pagination.totalElements} companies
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('prospector_dont_redirect', 'true');
                  setShowSearchForm(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                New Search
              </button>
              <button
                type="button"
                onClick={() => setShowBulkSave(!showBulkSave)}
                disabled={bulkSaving}
                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  bulkSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
              >
                {bulkSaving ? 'Saving...' : `Save All ${pagination.totalElements} to List`}
              </button>
            </div>
          </div>
          
          {showBulkSave && (
            <div className="px-4 py-3 sm:px-6 bg-blue-50 border-b border-gray-200">
              <div className="max-w-md">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Save all {pagination.totalElements} companies to a list
                </h3>
                <BulkSaveComponent
                  companiesCount={pagination.totalElements}
                  onSave={handleBulkSaveToList}
                  onCancel={() => setShowBulkSave(false)}
                  isLoading={bulkSaving}
                  searchParams={currentSearchParams}
                />
              </div>
            </div>
          )}
          <ul className="divide-y divide-gray-200">
            {searchResults.map((company) => (
              <li key={company.organisasjonsnummer} className="hover:bg-gray-50 cursor-pointer">
                <div className="px-4 py-4 sm:px-6" onClick={() => handleViewCompany(company.organisasjonsnummer)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-blue-600 truncate">
                        {company.navn}
                      </p>
                      <p className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {company.organisasjonsnummer}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the parent onClick
                          handleAddToList(company);
                        }}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Add to List
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        {company.naeringskode1 && (
                          <>
                            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3v-7a1 1 0 00-1-1H7a1 1 0 00-1 1v7H3a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                            </svg>
                            {company.naeringskode1.beskrivelse}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                      {company.antallAnsatte !== undefined ? `${company.antallAnsatte} employees` : 'Employees not specified'}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {pagination.totalPages > 1 && (
            <nav className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:block">
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.currentPage + 1}</span> of <span className="font-medium">{pagination.totalPages}</span>
                </p>
              </div>
              <div className="flex-1 flex justify-between sm:justify-end">
                <button
                  disabled={pagination.currentPage === 0}
                  onClick={() => {
                    // Create a new params object with the previous page
                    const newParams = {
                      ...(currentSearchParams || {}),
                      side: pagination.currentPage - 1,
                      sidestorrelse: pagination.pageSize
                    };
                    // Clear previous saved results before new search
                    localStorage.removeItem('prospector_search_results');
                    handleSearch(newParams);
                  }}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.currentPage >= pagination.totalPages - 1}
                  onClick={() => {
                    // Create a new params object with the next page
                    const newParams = {
                      ...(currentSearchParams || {}),
                      side: pagination.currentPage + 1,
                      sidestorrelse: pagination.pageSize
                    };
                    // Clear previous saved results before new search
                    localStorage.removeItem('prospector_search_results');
                    handleSearch(newParams);
                  }}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </nav>
          )}
        </div>
      ) : !isLoading && (
        <div className="bg-white p-6 shadow rounded-md text-center">
          <p className="text-gray-500">Use the search form above to find companies.</p>
        </div>
      )}

      {/* Company details are now shown in a separate page at /prospector/:orgNumber */}

      {/* SaveCompany Modal */}
      {showSaveCompany && activeCompany && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Add {activeCompany.navn} to List
                </h3>
                <button
                  onClick={() => {
                    setShowSaveCompany(false);
                    setActiveCompany(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mt-4">
                <SaveCompany
                  company={activeCompany}
                  onSuccess={handleSaveCompanySuccess}
                  isDropdown={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prospector;